"use client";

// useNaturalHeight — publish an element's NATURAL (unfolded) height as a CSS
// variable on the element itself.
//
// Why (law_scroll_lock): a locked head folds by collapsing `max-height` from its
// own height down to zero, and a display title collapses by scaling with a
// matching height. Both need one number CSS cannot ask for — how tall the thing
// would be if nothing were collapsing it.
//
// `scrollHeight` is that number — but ONLY while nothing is constraining the
// box. `max-height` doesn't change it; an explicit `height` does: once the title
// is sized from `--lock-h`, `scrollHeight` reports that height back, the value
// latches at whatever the first (possibly mid-layout) measure happened to be,
// and it can never come back down. So the measure CLEARS the variable first,
// reads the natural height, and writes it back. That costs one synchronous
// reflow — but only on resize or a content change, never on the scroll path.
//
// ⚠️ THE MEASURE MUST NOT RUN WHILE THE FOLD IS PLAYING. This is the reader's
// jitter, and it was here rather than in the easing.
//
// The element being measured is the element being collapsed, so its own
// ResizeObserver fires on every frame of the 0.5s `max-height` transition. Each
// of those callbacks removed `--lock-h` (making the `max-height` calc invalid,
// which falls the property back to `none` mid-transition), read `scrollHeight`,
// and wrote the variable again. Re-writing the value a running transition is
// interpolating TOWARD retargets it: the browser restarts the interpolation from
// wherever it currently is, ~30 times over half a second. That is the stutter,
// the head that appears to stall halfway, and the headline that clashes with the
// words rising past it.
//
// So a measure is skipped outright while a transition owns the box, and the
// write is skipped when the value hasn't moved. A fold changes nothing about the
// NATURAL height by definition — that is what natural means — so there is
// nothing to learn from measuring during one.
//
// The variable lands on the ELEMENT, not on :root — several locked heads can be
// mounted at once (a shell inside a shell) without clobbering each other.

import { useEffect, type RefObject } from "react";

export function useNaturalHeight(
  ref: RefObject<HTMLElement | null>,
  varName: string,
  /** re-measure when this changes (content swapped without a resize) */
  key?: unknown,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let last = -1;
    let frame = 0;
    // how many transitions are currently running on this box. The fold animates
    // max-height (or height, on a settling title); while either is in flight the
    // element's size is not its natural size and must not be sampled.
    let animating = 0;
    const measure = () => {
      frame = 0;
      if (animating > 0) return;
      // a box with no width has no meaningful height — text in it wraps one word
      // per line. That happens whenever the surface is laid out while hidden (a
      // background tab, a display:none ancestor), and latching THAT number would
      // leave a head folding down from an absurd height. Wait for a real box; the
      // ResizeObserver brings us back the moment there is one.
      if (el.clientWidth === 0) return;
      // unconstrain, read, restore — see the note above.
      el.style.removeProperty(varName);
      const h = el.scrollHeight;
      if (h > 0) last = h;
      if (last > 0) el.style.setProperty(varName, `${last}px`);
      // a DETACHED head (lib/os/lockFold) needs the same number one level up:
      // the siblings that ride up by it, the band that shortens by it, and the
      // band that shortens by it read it from the head, not the fold. The column
      // gap goes with it, in px, so the ride is exactly one gap short.
      const head = el.closest<HTMLElement>(".os-lock--detached");
      if (head && last > 0) {
        head.style.setProperty(varName, `${last}px`);
        const gap = el.parentElement ? parseFloat(getComputedStyle(el.parentElement).rowGap) || 0 : 0;
        head.style.setProperty("--lock-gap", `${gap}px`);
      }
      // on the stage the content's edge rises by the same amount (and a split
      // stage's fold asks how much it gives up), and the content is the head's
      // sibling: the stage root carries it for both, detached head or not
      const stage = el.closest<HTMLElement>("[data-stage]");
      if (stage && last > 0) {
        stage.style.setProperty(varName, `${last}px`);
        const gap = el.parentElement ? parseFloat(getComputedStyle(el.parentElement).rowGap) || 0 : 0;
        stage.style.setProperty("--lock-gap", `${gap}px`);
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    // only the box-size properties matter; a colour or opacity transition on the
    // same element says nothing about its height. On the stage the fold's height
    // is driven by the HEAD's --lock-fold, so that transition counts too.
    const SIZED = new Set(["max-height", "height", "--lock-fold"]);
    const head0 = el.closest<HTMLElement>(".os-lock--detached");
    const mine = (t: TransitionEvent) => t.target === el || (t.target === head0 && t.propertyName === "--lock-fold");
    const onStart = (e: Event) => {
      const t = e as TransitionEvent;
      if (mine(t) && SIZED.has(t.propertyName)) animating++;
    };
    const onEnd = (e: Event) => {
      const t = e as TransitionEvent;
      if (mine(t) && SIZED.has(t.propertyName)) {
        animating = Math.max(0, animating - 1);
        // the box has settled — take one honest measure of where it landed.
        if (animating === 0) schedule();
      }
    };
    el.addEventListener("transitionrun", onStart);
    el.addEventListener("transitionend", onEnd);
    el.addEventListener("transitioncancel", onEnd);
    if (head0 && head0 !== el) {
      head0.addEventListener("transitionrun", onStart);
      head0.addEventListener("transitionend", onEnd);
      head0.addEventListener("transitioncancel", onEnd);
    }
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(el);
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro?.disconnect();
      window.removeEventListener("resize", schedule);
      el.removeEventListener("transitionrun", onStart);
      el.removeEventListener("transitionend", onEnd);
      el.removeEventListener("transitioncancel", onEnd);
      if (head0 && head0 !== el) {
        head0.removeEventListener("transitionrun", onStart);
        head0.removeEventListener("transitionend", onEnd);
        head0.removeEventListener("transitioncancel", onEnd);
      }
      el.style.removeProperty(varName);
      const head = el.closest<HTMLElement>(".os-lock--detached");
      if (head) { head.style.removeProperty(varName); head.style.removeProperty("--lock-gap"); }
      const stage = el.closest<HTMLElement>("[data-stage]");
      if (stage) { stage.style.removeProperty(varName); stage.style.removeProperty("--lock-gap"); }
    };
  }, [ref, varName, key]);
}
